import { Banner } from '@neo4j-ndl/react';
import { type AuraDatabase, consoleApi, useActiveProject } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { DataGridHelpers } from '@nx/ui';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { memo, useMemo } from 'react';

import { DatabaseTableRowActions } from './database-actions';
import { DatabaseStatus } from './database-status';

const numberFormatter = new Intl.NumberFormat();

interface Props {
  instanceId: string;
  filter?: string;
}

const InstanceDatabasesListInternal = ({ instanceId, filter = '' }: Props) => {
  const activeProject = useActiveProject();
  const { data, isLoading, isError } = consoleApi.useListDatabasesQuery(instanceId, {
    pollingInterval: 45000,
    refetchOnMountOrArgChange: 10,
    skipPollingIfUnfocused: true,
  });

  const formatCount = (count?: number): string => {
    return isNotNullish(count) ? numberFormatter.format(count) : '--';
  };

  const columnHelper = createColumnHelper<AuraDatabase>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (cx) => <DataGridHelpers.TruncatedColumn value={cx.getValue()} />,
        minSize: 100,
        size: 160,
        maxSize: 300,
      }),
      columnHelper.accessor('nodes', {
        header: 'Nodes',
        cell: (ctx) => formatCount(ctx.getValue()),
        maxSize: 130,
        size: 80,
        minSize: 80,
      }),
      columnHelper.accessor('relationships', {
        header: 'Relationships',
        cell: (ctx) => formatCount(ctx.getValue()),
        maxSize: 150,
        size: 140,
        minSize: 100,
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        maxSize: 200,
        size: 180,
        minSize: 100,
        cell: (ctx) => <DatabaseStatus database={ctx.row.original} />,
      }),
      columnHelper.display({
        id: 'actions',
        cell: (ctx) => {
          const database = ctx.row.original;
          return <DatabaseTableRowActions database={database} project={activeProject} />;
        },
        minSize: 100,
        size: 100,
        maxSize: 200,
        enableResizing: false,
      }),
    ],
    [activeProject, columnHelper],
  );

  const filteredDatabases = useMemo(() => {
    const normalizedFilter = filter.trim().toLocaleLowerCase();
    return isNotNullish(data) ? data.filter(({ name }) => name.toLocaleLowerCase().includes(normalizedFilter)) : [];
  }, [data, filter]);

  const table = useReactTable({
    columns,
    data: filteredDatabases,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    enableColumnPinning: true,
    state: {
      columnPinning: {
        right: ['actions'],
      },
    },
  });

  return (
    <>
      {isError ? (
        <Banner
          type="danger"
          description="Something went wrong while fetching the databases. Please try again."
          usage="inline"
        />
      ) : (
        <DataGridHelpers.Wrapper>
          <DataGridHelpers.DataGridRightColumnPinned
            tableInstance={table}
            isLoading={isLoading}
            isResizable={false}
            styling={{ headerStyle: 'clean' }}
            isAutoResizingColumns={true}
          />
        </DataGridHelpers.Wrapper>
      )}
    </>
  );
};

export const InstanceDatabasesList = memo(InstanceDatabasesListInternal);
